import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, makeStyles, Typography, Box } from "@material-ui/core";
import { green, red, orange } from "@material-ui/core/colors";
import { CheckCircle, Error as ErrorIcon, Sync } from "@material-ui/icons";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { isNil } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import ConnectionIcon from "../ConnectionIcon";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
  connectionOption: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1),
  },
  connectionName: {
    flex: 1,
  },
  connectionStatus: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.875rem",
  },
  statusConnected: {
    color: green[600],
  },
  statusDisconnected: {
    color: red[600],
  },
  statusConnecting: {
    color: orange[600],
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticketIds, ticket, onSuccess }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState('');
  const [companySettings, setCompanySettings] = useState({});
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useCompanySettings();
  const [connections, setConnections] = useState([]);
  const [myConnections, setMyConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const pickDefaultConnection = (list, preferredId) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    if (preferredId) {
      const preferred = list.find((conn) => conn.id === preferredId);
      if (preferred) return preferred;
    }
    return list[0] || null;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!modalOpen || !isMounted.current) return;
    const loadQueues = async () => {
      const list = await findAllQueues();
      setAllQueues(list);
      setQueues(list);
    };
    loadQueues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    const fetchCompanySettings = async () => {
      if (!user || !user.companyId) return;
      try {
        const settings = await getAllSettings(user.companyId);
        setCompanySettings(settings);
      } catch (err) {
        toastError(err);
      }
    };
    fetchCompanySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, user]);

  // Carregar conexões do usuário logado (fallback quando não há destino)
  useEffect(() => {
    const fetchConnections = async () => {
      if (!user || !user.companyId || !modalOpen) return;
      setLoadingConnections(true);
      try {
        const { data } = await api.get("/whatsapp/");
        const companyConnections = data.filter(
          (conn) => conn.companyId === user.companyId
        );
        setMyConnections(companyConnections);

        if (!selectedUser) {
          setConnections(companyConnections);
          if (ticket?.whatsappId) {
            setSelectedConnection(
              pickDefaultConnection(companyConnections, ticket.whatsappId)
            );
          } else {
            setSelectedConnection(null);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar conexões:", err);
        toastError(err);
      } finally {
        setLoadingConnections(false);
      }
    };

    if (modalOpen) {
      fetchConnections();
    } else {
      setSelectedConnection(null);
      setConnections([]);
      setMyConnections([]);
      setSelectedUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, user, ticket?.whatsappId]);

  // Ao selecionar usuário de destino, mostrar as conexões permitidas dele
  useEffect(() => {
    if (!modalOpen) return;

    if (selectedUser) {
      const destConnections =
        Array.isArray(selectedUser.whatsapps) && selectedUser.whatsapps.length > 0
          ? selectedUser.whatsapps
          : myConnections;

      setConnections(destConnections);
      setSelectedConnection(
        pickDefaultConnection(
          destConnections,
          selectedUser.whatsappId || ticket?.whatsappId
        )
      );
      return;
    }

    setConnections(myConnections);
    setSelectedConnection(
      pickDefaultConnection(myConnections, ticket?.whatsappId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, myConnections, modalOpen, ticket?.whatsappId]);


  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, showAll: true },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
    setSelectedConnection(null);
  };

  // ✅ NOVO: Função para obter o ícone e cor do status da conexão
  const getConnectionStatusDisplay = (status) => {
    const normalizedStatus = status?.toUpperCase() || "DISCONNECTED";
    
    if (normalizedStatus === "CONNECTED") {
      return {
        icon: <CheckCircle style={{ fontSize: 16, color: green[600] }} />,
        text: "CONNECTED",
        color: green[600],
      };
    } else if (["PAIRING", "OPENING", "QRCODE"].includes(normalizedStatus)) {
      return {
        icon: <Sync style={{ fontSize: 16, color: orange[600] }} />,
        text: normalizedStatus,
        color: orange[600],
      };
    } else {
      return {
        icon: <ErrorIcon style={{ fontSize: 16, color: red[600] }} />,
        text: "DISCONNECTED",
        color: red[600],
      };
    }
  };

  const handleSaveTicket = async (e) => {
    // e.preventDefault();
    const ids = Array.isArray(ticketIds) && ticketIds.length
      ? ticketIds
      : ticketid
        ? [ticketid]
        : [];
    if (!ids.length) return;
    if (!selectedQueue || selectedQueue === "") return;
    setLoading(true);
    try {
      const transferStatus = companySettings.transferredTicketStatus || "open";

      for (const id of ids) {
        const data = {};
        data.userId = !selectedUser ? null : selectedUser.id;
        data.status = !selectedUser
          ? "pending"
          : ticket?.isGroup
            ? "group"
            : transferStatus;
        data.queueId = selectedQueue;
        data.msgTransfer = msgTransfer ? msgTransfer : null;
        data.isTransfered = true;

        if (selectedConnection && selectedConnection.id) {
          data.whatsappId = selectedConnection.id;
        }

        await api.put(`/tickets/${id}`, data);
      }

      setLoading(false);
      if (typeof onSuccess === "function") {
        onSuccess();
        handleClose();
      } else {
        history.push(`/tickets/`);
        handleClose();
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      {/* <form onSubmit={handleSaveTicket}> */}
      <DialogTitle id="form-dialog-title">
        {Array.isArray(ticketIds) && ticketIds.length > 1
          ? `${i18n.t("transferTicketModal.title")} (${ticketIds.length})`
          : i18n.t("transferTicketModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} xl={6}>
            <Autocomplete
              fullWidth
              getOptionLabel={(option) => `${option.name}`}
              onChange={(e, newValue) => {
                setSelectedUser(newValue);
                if (newValue != null && Array.isArray(newValue.queues)) {
                  if (newValue.queues.length === 1) {
                    setSelectedQueue(newValue.queues[0].id);
                  }
                  setQueues(newValue.queues);

                } else {
                  setQueues(allQueues);
                  setSelectedQueue("");
                }
              }}
              options={options}
              filterOptions={filterOptions}
              freeSolo
              autoHighlight
              noOptionsText={i18n.t("transferTicketModal.noOptions")}
              loading={loading}
              renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("transferTicketModal.fieldLabel")}
                  variant="outlined"
                  autoFocus
                  onChange={(e) => setSearchParam(e.target.value)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid xs={12} sm={6} xl={6} item >
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldQueueLabel")}
              </InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
              >
                {[...queues]
                  .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                  .map((queue) => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} xl={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldConnectionLabel") || "Conexão de Destino"}
              </InputLabel>
              <Select
                value={selectedConnection?.id || ""}
                onChange={(e) => {
                  const connectionId = e.target.value;
                  const connection = connections.find((c) => c.id === connectionId);
                  setSelectedConnection(connection || null);
                }}
                label={i18n.t("transferTicketModal.fieldConnectionLabel") || "Conexão de Destino"}
                disabled={loadingConnections}
              >
                <MenuItem value="">
                  <em>
                    {i18n.t("transferTicketModal.fieldConnectionDefault") || 
                     "Manter conexão atual"}
                  </em>
                </MenuItem>
                {connections
                  .sort((a, b) => {
                    // Ordenar: CONNECTED primeiro, depois por nome
                    if (a.status === "CONNECTED" && b.status !== "CONNECTED") return -1;
                    if (a.status !== "CONNECTED" && b.status === "CONNECTED") return 1;
                    return (a.name || `Conexão ${a.id}`).localeCompare(
                      b.name || `Conexão ${b.id}`,
                      "pt-BR",
                      { sensitivity: "base" }
                    );
                  })
                  .map((connection) => {
                    const statusDisplay = getConnectionStatusDisplay(connection.status);
                    return (
                      <MenuItem key={connection.id} value={connection.id}>
                        <Box className={classes.connectionOption}>
                          <ConnectionIcon
                            connectionType={connection.channel || "whatsapp"}
                          />
                          <Typography className={classes.connectionName}>
                            {connection.name || `Conexão ${connection.id}`}
                          </Typography>
                          <Box
                            className={classes.connectionStatus}
                            style={{ color: statusDisplay.color }}
                          >
                            {statusDisplay.icon}
                            <Typography variant="caption">
                              {statusDisplay.text}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} xl={12} >
            <TextField
              label={i18n.t("transferTicketModal.msgTransfer")}
              value={msgTransfer}
              onChange={handleMsgTransferChange}
              variant="outlined"
              multiline
              maxRows={5}
              minRows={5}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="submit"
          color="primary"
          loading={loading}
          disabled={selectedQueue === ""}
          onClick={() => handleSaveTicket(selectedQueue)}

        >
          {i18n.t("transferTicketModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
      {/* </form> */}
    </Dialog>
  );
};

export default TransferTicketModalCustom;
